export interface BillingProduct {
  productId: string;
  title: string;
  description?: string;
  price: string;
  priceMicros: number;
  currencyCode: string;
  billingPeriod: string;
  trialPeriod: string | null;
  offerToken: string | null;
  trialPeriodDays?: string | null;
  billingPeriodDays?: string | null;
  nextPaymentDay?: string | null;
}

export interface ActiveSubscription {
  productId: string;
  productName: string;
  statusCode: "active" | "payment_failed" | "canceled";
  remainingDays: number;
  nextBillingDate: string;
  expectedAmount: number;
  gracePeriodEndDate: string | null;
  autoRenewing: boolean;
}

export interface ActiveSubscriptionsResponse {
  userId: string;
  subscriptions: ActiveSubscription[];
}

export interface UserSubscriptionStatus {
  userId: string;
  subscriptionStatus:
    | "active"
    | "canceled"
    | "payment_failed"
    | "expired"
    | "none";
  productId: string;
  startDate: string;
  expiryDate: string;
  autoRenewing: boolean;
}

const getApiDomain = () => {
  return process.env.REACT_APP_DSAPI_URL || ""; // 환경변수에서 API 도메인을 가져옵니다. 없으면 빈 문자열을 반환합니다.
};

const GOOGLE_PLAY_API_BASE_PATH = "/api/popcorn/google-play/v1";

export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  } catch {
    return "-";
  }
};

export const SubscriptionApi = {
  getUrl: (path: string) =>
    `${getApiDomain()}${GOOGLE_PLAY_API_BASE_PATH}${path}`,

  get: (path: string, init?: RequestInit) =>
    fetch(`${getApiDomain()}${GOOGLE_PLAY_API_BASE_PATH}${path}`, {
      method: "GET",
      credentials: "include",
      ...init,
    }),

  post: (path: string, body?: unknown, init?: RequestInit) =>
    fetch(`${getApiDomain()}${GOOGLE_PLAY_API_BASE_PATH}${path}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers || {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...init,
    }),
};

export const SubscriptionUtils = {
  parseProductData: (data: BillingProduct): BillingProduct => {
    // data 에서 trialPeriod이 설정되어 있으면 trialPeriodDays 계산
    let billingStartDate = new Date();
    let trialEndDate: Date | null = null;
    let billingEndDate: Date | null = null;

    if (data.trialPeriod) {
      const match = data.trialPeriod.match(/P(\d+)D/);
      if (match) {
        const days = parseInt(match[1], 10);
        const now = new Date();
        trialEndDate = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        };
        data.trialPeriodDays = `${now.toLocaleDateString(
          "ko-KR",
          options,
        )} ~ ${trialEndDate.toLocaleDateString("ko-KR", options)}`;

        // billingPeriod 시작날을 trial 종료 다음날로 설정
        billingStartDate = new Date(trialEndDate);
        billingStartDate.setDate(billingStartDate.getDate() + 1);
      } else {
        data.trialPeriodDays = null;
      }
    }

    // billingPeriod 계산
    // 형식: P1M, P1Y, P1D 등
    if (data.billingPeriod) {
      const match = data.billingPeriod.match(/P(\d+)([DMY])/);
      if (match) {
        const amount = parseInt(match[1], 10);
        const unit = match[2]; // D(Day), M(Month), Y(Year)

        billingEndDate = new Date(billingStartDate);

        // 단위에 따라 계산
        if (unit === "D") {
          billingEndDate.setDate(billingEndDate.getDate() + amount);
        } else if (unit === "M") {
          billingEndDate.setMonth(billingEndDate.getMonth() + amount);
        } else if (unit === "Y") {
          billingEndDate.setFullYear(billingEndDate.getFullYear() + amount);
        }

        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        };

        data.billingPeriodDays = `${billingStartDate.toLocaleDateString(
          "ko-KR",
          options,
        )} ~ ${billingEndDate.toLocaleDateString("ko-KR", options)}`;
      } else {
        data.billingPeriodDays = null;
      }
    }

    // nextPaymentDay 계산
    // 만약에 trialPeriod이 있으면 trial이 끝나는 다음 날이 다음 결제일
    // billingPeriod이 있으면 billingPeriod이 끝나는 날이 다음 결제일
    // 둘 다 없으면 null
    if (trialEndDate) {
      // trial이 있는 경우: trial 다음날
      const nextPaymentDate = new Date(trialEndDate);
      nextPaymentDate.setDate(nextPaymentDate.getDate() + 1);
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      data.nextPaymentDay = nextPaymentDate.toLocaleDateString(
        "ko-KR",
        options,
      );
    } else if (billingEndDate) {
      // trial이 없고 billing period이 있는 경우: billing 종료일
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      data.nextPaymentDay = billingEndDate.toLocaleDateString("ko-KR", options);
    } else {
      // 둘 다 없는 경우: null
      data.nextPaymentDay = null;
    }

    return data;
  },
};
